import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/authContext';
import { useNavigate } from 'react-router-dom';
import Helmet from '../components/Helmet/Helmet';
import { getApiUrl } from '../utils/site';

const DesignRequest = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState('');
  const [customName, setCustomName] = useState('');
  const [customImage, setCustomImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        const res = await axios.get(getApiUrl('/api/designs'));
        setDesigns(res.data.data || res.data);
      } catch (err) {
        // ignore
      }
    };
    fetchDesigns();
  }, []);

  useEffect(() => {
    if (!user) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, navigate]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setCustomImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    // Frontend validation
    if (!customerName.trim()) {
      setError('Your name is required.');
      return;
    }
    if (!selectedDesignId && !customName.trim()) {
      setError('Please select a design from the gallery or enter a custom design name.');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      if (selectedDesignId) formData.append('design_id', selectedDesignId);
      if (customName) formData.append('design_name', customName);
      if (customImage) formData.append('design_image_url', customImage);
      if (notes) formData.append('notes', notes);
      formData.append('name', customerName);
      if (customerEmail) formData.append('email', customerEmail);
      if (customerPhone) formData.append('phone', customerPhone);
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl('/api/design-requests'), formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccess('Your design request has been submitted!');
      setSelectedDesignId('');
      setCustomName('');
      setCustomImage(null);
      setImagePreview(null);
      setNotes('');
      setCustomerName('');
      setCustomerEmail('');
      setCustomerPhone('');
    } catch (err) {
      setError('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-warning p-4">
          <h4 className="mb-3">Please login to request a custom design.<br/>Redirecting to login...</h4>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Custom Design Request | Ethiopian Gemstone Jewelry | Roha Jewellery</title>
        <meta name="description" content="🌟 Request your custom Ethiopian gemstone jewelry design! Create unique opal, emerald, ruby & gold jewelry pieces with our master artisans. Upload your design or choose from our gallery. Free consultation!" />
        <meta name="keywords" content="custom jewelry design Ethiopia, Ethiopian gemstone custom jewelry, custom opal jewelry, custom emerald jewelry, custom ruby jewelry, bespoke jewelry Ethiopia, jewelry design consultation, Roha Jewellery custom design" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://rohajewellery.com/design-request" />
        <meta property="og:title" content="Custom Design Request | Ethiopian Gemstone Jewelry" />
        <meta property="og:description" content="🌟 Request your custom Ethiopian gemstone jewelry design! Create unique opal, emerald, ruby & gold jewelry pieces with our master artisans." />
        <meta property="og:url" content="https://rohajewellery.com/design-request" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow-lg border-0 rounded-4 p-4">
            <h2 className="fw-bold mb-4 text-primary text-center">Request a Custom Design</h2>
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="mb-4">
                <h5 className="mb-2">1. Select a Design from Gallery <span className="text-muted">(optional)</span></h5>
                <select className="form-select" value={selectedDesignId} onChange={e => setSelectedDesignId(e.target.value)}>
                  <option value="">-- Select Design --</option>
                  {designs.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              </div>
              <div className="mb-4">
                <h5 className="mb-2">2. Or Upload Your Own Design</h5>
                <input type="text" className="form-control mb-2" placeholder="Design Name" value={customName} onChange={e => setCustomName(e.target.value)} />
                <input type="file" className="form-control" accept="image/*" onChange={handleImageChange} />
                {imagePreview && (
                  <div className="mt-2">
                    <img src={imagePreview} alt="Preview" style={{ maxWidth: 180, maxHeight: 180, borderRadius: 8, border: '1px solid #eee' }} />
                  </div>
                )}
              </div>
              <div className="mb-4">
                <h5 className="mb-2">3. Your Contact Information</h5>
                <input type="text" className="form-control mb-2" placeholder="Your Name (required)" value={customerName} onChange={e => setCustomerName(e.target.value)} required />
                <input type="email" className="form-control mb-2" placeholder="Your Email (optional)" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} />
                <input type="text" className="form-control mb-2" placeholder="Your Phone (optional)" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
              </div>
              <div className="mb-4">
                <h5 className="mb-2">4. Additional Notes</h5>
                <textarea className="form-control" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe your idea, preferred materials, etc." rows={3} />
              </div>
              {error && <div className="alert alert-danger text-center">{error}</div>}
              {success && <div className="alert alert-success text-center">{success}</div>}
              <div className="d-grid">
                <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>{loading ? 'Submitting...' : 'Submit Request'}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default DesignRequest;

